/**
 * Accessible Button component with loading states and variants
 */

import React, { forwardRef } from 'react'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'icon'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant */
  variant?: ButtonVariant
  /** Size preset */
  size?: ButtonSize
  /** Show loading spinner */
  loading?: boolean
  /** Loading text for screen readers */
  loadingText?: string
  /** Icon to display (for icon-only buttons, aria-label is required) */
  icon?: React.ReactNode
  /** Left icon */
  leftIcon?: React.ReactNode
  /** Right icon */
  rightIcon?: React.ReactNode
  /** Full width button */
  fullWidth?: boolean
}

/**
 * Accessible button with press feedback, loading states, and variants
 *
 * @example
 * // Primary button
 * <Button variant="primary">Save</Button>
 *
 * @example
 * // Icon-only button (aria-label required)
 * <Button variant="icon" aria-label="Delete item" icon={<TrashIcon />} />
 *
 * @example
 * // Loading state
 * <Button loading loadingText="Saving...">Save</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingText = 'Loading...',
      icon,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      className = '',
      children,
      'aria-label': ariaLabel,
      ...props
    },
    ref
  ) {
    // Icon-only buttons require aria-label
    const isIconOnly = variant === 'icon' || (icon && !children)
    if (isIconOnly && !ariaLabel) {
      console.warn('Button: Icon-only buttons require an aria-label for accessibility')
    }

    const isDisabled = disabled || loading

    // Build class name
    const classes = [
      'btn',
      `btn-${variant}`,
      `btn-${size}`,
      fullWidth && 'btn-full',
      loading && 'btn-loading',
      isIconOnly && 'btn-icon-only',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <button
        ref={ref}
        className={classes}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        aria-label={ariaLabel}
        {...props}
      >
        {loading ? (
          <>
            <span className="btn-spinner" aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  stroke="currentColor"
                  strokeOpacity="0.25"
                  strokeWidth="2"
                />
                <path
                  d="M14 8a6 6 0 00-6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="sr-only">{loadingText}</span>
            {!isIconOnly && <span className="btn-text">{children}</span>}
          </>
        ) : (
          <>
            {leftIcon && <span className="btn-icon btn-icon-left" aria-hidden="true">{leftIcon}</span>}
            {icon && <span className="btn-icon" aria-hidden="true">{icon}</span>}
            {children && <span className="btn-text">{children}</span>}
            {rightIcon && <span className="btn-icon btn-icon-right" aria-hidden="true">{rightIcon}</span>}
          </>
        )}
      </button>
    )
  }
)

// CSS for Button component (add to styles.css)
export const buttonStyles = `
/* ============================================
   BUTTON COMPONENT
   ============================================ */

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.btn:disabled,
.btn[aria-disabled="true"] {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/* Sizes */
.btn-sm {
  padding: 8px 14px;
  font-size: 13px;
  min-height: 36px;
}

.btn-md {
  padding: 10px 18px;
  font-size: 14px;
  min-height: 44px;
}

.btn-lg {
  padding: 14px 24px;
  font-size: 16px;
  min-height: 52px;
}

/* Variants */
.btn-primary {
  background: var(--accent);
  color: var(--bg);
}

.btn-primary:hover:not(:disabled) {
  box-shadow: 0 0 20px var(--accent-glow);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.06);
  color: var(--text);
  border: 1px solid var(--border);
}

.btn-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--accent);
}

.btn-ghost {
  background: transparent;
  color: var(--muted);
}

.btn-ghost:hover:not(:disabled) {
  color: var(--text);
  background: rgba(255, 255, 255, 0.04);
}

.btn-danger {
  background: var(--red);
  color: white;
}

.btn-danger:hover:not(:disabled) {
  box-shadow: 0 0 20px rgba(239, 68, 68, 0.3);
}

/* Icon-only button */
.btn-icon-only {
  padding: 10px;
  min-width: 44px;
  min-height: 44px;
}

.btn-icon-only.btn-sm {
  padding: 8px;
  min-width: 36px;
  min-height: 36px;
}

/* Full width */
.btn-full {
  width: 100%;
}

/* Loading spinner */
.btn-spinner {
  display: inline-flex;
  animation: spin 0.8s linear infinite;
}

.btn-loading .btn-text {
  opacity: 0.7;
}

/* Icon positioning */
.btn-icon {
  display: inline-flex;
  flex-shrink: 0;
}

.btn-icon-left {
  margin-right: -2px;
}

.btn-icon-right {
  margin-left: -2px;
}
`

export default Button
